import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AppDispatch, AppState, AppStore } from '../../../store';
import {
  EAction,
  EActionType,
  EApplicability,
  EFormFieldType,
  ELovValues,
  EPropertyOrigin,
  EPropertyType,
  excludeLovValues,
  RegExPatterns,
} from '../../../data/Constants';
import {
  Box,
  Collapse,
  Alert,
  AlertTitle,
  TextField,
  Button,
  RadioGroup,
  Grid,
  FormControlLabel,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  IconButton,
  InputAdornment,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { ErrorMessage } from '../../ErrorMessage';
import { Control, Controller, useForm } from 'react-hook-form';
import {
  getEnumOptions,
  resetAllErrorInfo,
  getErrorMessage,
} from '../../../services/DataHelperFunctions';
import {
  createProperty,
  getProperties,
  getPropertyDetails,
  updateProperty,
} from '../../../store/states/PropertySlice';
import { setAlertMessage } from '../../../store/states/AlertMessageSlice';
import { resetDialogMessage } from '../../../store/states/DialogSlice';
import { resetError } from '../../../store/states/ErrorSlice';
import { TFunction } from 'i18next';
import { CheckboxGroup } from '../../Layouts/CheckboxGroup';

import type {
  IError,
  IManipulationProps,
  IEnumOption,
  IEnumValue,
  IPropertyData,
  IAPIResponse,
  IFormData,
} from '../../../../types';

const stringValidations = {
  code: {
    rules: {
      minLength: { value: 3, message: 'minLimitErrorMessage' },
      maxLength: { value: 4, message: 'maxLimitErrorMessage' },
    },
  },
  name: {
    rules: {
      minLength: { value: 3, message: 'minLimitErrorMessage' },
      maxLength: { value: 50, message: 'maxLimitErrorMessage' },
    },
  },
};

//Call the create or update api call to update or create property
function callCreateOrUpdateApi(
  manipulationType: string,
  props: IManipulationProps,
  formData: IPropertyData,
  t: TFunction,
  dispatch: AppDispatch
): void {
  if (manipulationType === EActionType.Edit) {
    dispatch(
      updateProperty({
        token: props.token,
        request: {
          ...formData,
          name: formData.name.trimEnd(),
          description: formData.description.trim(),
        },
      })
    ).then((res) => {
      handleApiResponse(
        res?.payload as IAPIResponse,
        t('messages.fail.update'),
        t('messages.success.update'),
        dispatch
      );
    });
  } else {
    dispatch(
      createProperty({
        token: props.token,
        request: {
          ...formData,
          name: formData.name.trimEnd(),
          description: formData.description.trim(),
        },
      })
    ).then((res) => {
      handleApiResponse(
        res?.payload as IAPIResponse,
        t('messages.fail.create'),
        t('messages.success.create'),
        dispatch
      );
    });
  }
}
//Getet the latest properties from the db after create or update the properties
function handleApiResponse(
  res: IAPIResponse,
  failMessage: string,
  successMessage: string,
  dispatch: AppDispatch
): void {
  const properties = AppStore.getState().dataMaintainance.properties;
  if (res?.data) {
    dispatch(
      getProperties({
        page: Number(properties.currentPage),
        limit: Number(properties.recordsPerPage),
        searchCode: properties.searchKey,
      })
    );
    dispatch(setAlertMessage({ show: true, message: successMessage, type: 'success' }));
    dispatch(resetDialogMessage());
  } else {
    const message = res?.error?.message ? res.error.message : failMessage;
    dispatch(setAlertMessage({ show: true, message: message, type: 'error' }));
  }
}

const loadFormData = async (
  props: IManipulationProps,
  setInitialFormData: React.Dispatch<React.SetStateAction<IPropertyData>>,
  setEnumOptions: (value: React.SetStateAction<IEnumOption[]>) => void,
  enumValues: IEnumValue[],
  reset: (values?: IFormData) => void,
  setOldFormData: React.Dispatch<React.SetStateAction<IPropertyData>>
) => {
  const res = await AppStore.dispatch(
    getPropertyDetails({ token: props.token, searchCode: props.id })
  ).unwrap();

  if (!('data' in res) || !res.data) {
    return;
  }
  const propertyDetails = res.data.properties[0];
  propertyDetails.default = propertyDetails.default === null ? '' : propertyDetails.default.code;
  setInitialFormData(propertyDetails);
  setOldFormData(propertyDetails);
  if (propertyDetails.values && propertyDetails.values !== '') {
    const options: IEnumOption[] = getEnumOptions(enumValues, propertyDetails.values);
    setEnumOptions(options);
  }
  reset(propertyDetails);
};

//Disable the other applicabilities if Master is selected and vice versa
const validateApplicability = (
  formData: IPropertyData,
  manipulationType: string,
  code: string
): boolean => {
  return (
    (manipulationType === EActionType.Edit && !formData.isActive) ||
    (code !== EApplicability.Master && formData.applicability.includes(EApplicability.Master))
  );
};

const renderDefaults = (
  formData: IPropertyData,
  types: IEnumOption[],
  manipulationType: string,
  t: TFunction,
  control: Control<IPropertyData>
) => {
  const BoolPropertyDefaults = ['True', 'False'];

  return (
    <>
      {formData.type === EPropertyType.Bool && (
        <fieldset className="inputFieldset w-100 mt-1em">
          <legend className="inputLegend" id="default">
            {t('labels.default')}
          </legend>
          {types.length > 0 ? (
            <Controller
              name="default"
              control={control}
              render={({ field }) => (
                <RadioGroup {...field}>
                  <Grid container>
                    {BoolPropertyDefaults?.map((value: string) => {
                      return (
                        <Grid key={value} className="pt-0" size={4}>
                          <FormControlLabel
                            value={value}
                            key={value}
                            control={
                              <Radio
                                disabled={
                                  manipulationType === EActionType.Edit && !formData.isActive
                                }
                              />
                            }
                            label={value}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </RadioGroup>
              )}
            />
          ) : null}
        </fieldset>
      )}
      {formData.type === EPropertyType.String && (
        <>
          <Controller
            name="default"
            control={control}
            render={({ field }) => (
              <TextField
                className="mt-1em"
                {...field}
                label={t('labels.default')}
                variant="outlined"
                size="small"
                fullWidth
                disabled={manipulationType === EActionType.Edit && !formData.isActive}
                slotProps={{
                  htmlInput: { 'data-testid': 'role-name-input' },
                }}
              />
            )}
          />

          <br />
        </>
      )}
      {formData.type === EPropertyType.Numeric && (
        <>
          <Controller
            name="default"
            control={control}
            render={({ field }) => (
              <TextField
                className="mt-1em"
                {...field}
                label={t('labels.default')}
                variant="outlined"
                size="small"
                fullWidth
                disabled={manipulationType === EActionType.Edit && !formData.isActive}
                slotProps={{
                  htmlInput: { type: 'number', 'data-testid': 'property-numeric' },
                }}
              />
            )}
          />

          <br />
        </>
      )}
    </>
  );
};

const renderEnumFields = (
  formData: IPropertyData,
  enumOptions: IEnumOption[],
  enumValues: IEnumValue[],
  manipulationType: string,
  setEnumOptions: (value: React.SetStateAction<IEnumOption[]>) => void,
  t: TFunction,
  control: Control<IPropertyData>
) => {
  const selectionTypes =
    enumValues?.find((value: IEnumValue) => value.code === ELovValues.ESCT)?.options ?? [];
  const validValues = enumValues?.filter((value) => !excludeLovValues.includes(value.code));
  return (
    <>
      <fieldset className="inputFieldset w-100 mt-1em">
        <legend className="inputLegend" id="selectionType">
          {t('labels.selectionType')}
          <span className="asterisk">*</span>
        </legend>
        {selectionTypes.length > 0 ? (
          <Controller
            name="selectionType"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <RadioGroup {...field}>
                <Grid container>
                  {selectionTypes?.map((value: IEnumOption) => {
                    return (
                      <Grid key={value.code} className="pt-0" size={4}>
                        <FormControlLabel
                          value={value.code}
                          key={value.code}
                          control={
                            <Radio
                              disabled={manipulationType === EActionType.Edit && !formData.isActive}
                            />
                          }
                          label={value.name}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </RadioGroup>
            )}
          />
        ) : null}
      </fieldset>
      <fieldset className="inputFieldset w-50 mt-1em">
        <legend className="inputLegend" id="listOfValues">
          {t('labels.listOfValues')}
        </legend>
        <Box className="d-flex flex-wrap pb-2">
          <FormControl
            required
            className="w-50"
            size="small"
            disabled={manipulationType === EActionType.Edit && !formData.isActive}
          >
            <InputLabel>{t('labels.valueType')}</InputLabel>

            <Controller
              name="values"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <Select
                  label={t('labels.valueType')}
                  color="primary"
                  {...field}
                  value={formData.values}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    field.onChange(selectedValue);
                    const options = getEnumOptions(enumValues, selectedValue);
                    setEnumOptions(options);
                  }}
                  endAdornment={
                    field.value &&
                    !(manipulationType === EActionType.Edit && !formData.isActive) ? (
                      <InputAdornment position="end" className="property-clear-icon-wrapper">
                        <IconButton size="small" onClick={() => field.onChange('')} edge="end">
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                >
                  {validValues.map((value) => (
                    <MenuItem key={value.code} value={value.code}>
                      {value.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
          <FormControl
            className="w-50 pl-2"
            size="small"
            disabled={manipulationType === EActionType.Edit && !formData.isActive}
          >
            <InputLabel>{t('labels.default')}</InputLabel>
            <Controller
              name="default"
              control={control}
              render={({ field }) => (
                <Select
                  color="primary"
                  label={t('labels.default')}
                  {...field}
                  endAdornment={
                    field.value &&
                    !(manipulationType === EActionType.Edit && !formData.isActive) ? (
                      <InputAdornment position="end" className="property-clear-icon-wrapper">
                        <IconButton size="small" onClick={() => field.onChange('')} edge="end">
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                >
                  {enumOptions.map((value) => (
                    <MenuItem value={value.code} key={value.code}>
                      {value.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </Box>
      </fieldset>
    </>
  );
};

export const PropertyManipulationForm = (props: IManipulationProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const error = useSelector((state: AppState) => state.error);
  const enumValues = useSelector((state: AppState) => state.dataMaintainance.enumListOfValues);
  const manipulationType = props.type;
  const initialData: IPropertyData = {
    code: '',
    name: '',
    type: '',
    description: '',
    values: '',
    default: null,
    applicability: [],
    origin: EPropertyOrigin.Pdm,
    selectionType: '',
    isActive: false,
  };
  const [initialFormData, setInitialFormData] = useState<IPropertyData>(initialData);
  const [oldFormData, setOldFormData] = useState<IPropertyData>(initialData);
  const [enumOptions, setEnumOptions] = useState<IEnumOption[]>([]);
  const [displayAlertMessage, setDisplayAlertMessage] = useState<boolean>(false);

  const types = getEnumOptions(enumValues, ELovValues.ETYP);
  const applicabilities = getEnumOptions(enumValues, ELovValues.EAPP);
  const {
    handleSubmit,
    control,
    reset,
    getValues,
    watch,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: 'onChange',
    defaultValues: initialFormData,
  });
  const selectedType = watch('type');

  useEffect(() => {
    if (manipulationType === EActionType.Edit) {
      loadFormData(props, setInitialFormData, setEnumOptions, enumValues, reset, setOldFormData);
    }
  }, []);
  const handleError = (error: IError, setDisplayAlertMessage: (show: boolean) => void) => {
    if (error.code && (error.action === EAction.Create || error.action === EAction.Update)) {
      setDisplayAlertMessage(true);
    }
  };
  useEffect(() => {
    handleError(error, setDisplayAlertMessage);
  }, [error]);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
  };
  const onSubmit = (data: IPropertyData) => {
    dispatch(resetError());
    setDisplayAlertMessage(false);
    callCreateOrUpdateApi(manipulationType, props, data, t, dispatch);
  };

  return (
    <Box className="manipulationBox ">
      <Box className="manipulationForm">
        <Collapse in={displayAlertMessage} className="show-alert">
          <Alert className="errorMessage" severity="error" onClose={handleClose}>
            <AlertTitle>
              <ErrorMessage error={error} manipulationType={manipulationType} />
            </AlertTitle>
          </Alert>
        </Collapse>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="code"
            control={control}
            rules={{
              required: { value: true, message: 'codeErrorMessage' },
              minLength: {
                value: stringValidations.code.rules.minLength.value,
                message: 'minLimitErrorMessage',
              },
              maxLength: {
                value: stringValidations.code.rules.maxLength.value,
                message: 'maxLimitErrorMessage',
              },
              pattern: {
                value: RegExPatterns.AplhaNumericHyphenUnderscore,
                message: 'codeErrorMessage',
              },
            }}
            render={({ field }) => (
              <TextField
                className="mt-1em"
                {...field}
                label={
                  <>
                    {t('labels.code')}
                    <span className="asterisk"> *</span>
                  </>
                }
                variant="outlined"
                size="small"
                fullWidth
                disabled={manipulationType === EActionType.Edit}
                slotProps={{
                  htmlInput: { 'data-testid': 'property-code-input' },
                }}
              />
            )}
          />

          <Collapse in={!!errors['code']}>
            <Alert className="errorMessage" severity="error">
              <AlertTitle>
                {getErrorMessage(errors, 'code', t, stringValidations.code.rules)}
              </AlertTitle>
            </Alert>
          </Collapse>

          <Controller
            name="name"
            control={control}
            rules={{
              required: { value: true, message: 'nameErrorMessage' },
              minLength: {
                value: stringValidations.name.rules.minLength.value,
                message: 'minLimitErrorMessage',
              },
              maxLength: {
                value: stringValidations.name.rules.maxLength.value,
                message: 'maxLimitErrorMessage',
              },
            }}
            render={({ field }) => (
              <TextField
                className="mt-1em"
                {...field}
                label={
                  <>
                    {t('labels.name')}
                    <span className="asterisk">*</span>
                  </>
                }
                variant="outlined"
                size="small"
                fullWidth
                disabled={manipulationType === EActionType.Edit}
                slotProps={{
                  htmlInput: { 'data-testid': 'property-name-input' },
                }}
              />
            )}
          />

          <Collapse in={!!errors['name']}>
            <Alert className="errorMessage" severity="error">
              <AlertTitle>
                {getErrorMessage(errors, 'name', t, stringValidations.name.rules)}
              </AlertTitle>
            </Alert>
          </Collapse>

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                className="mt-1em"
                {...field}
                label={t('labels.description')}
                variant="outlined"
                size="small"
                fullWidth
                disabled={manipulationType === EActionType.Edit && !getValues('isActive')}
                slotProps={{
                  htmlInput: { 'data-testid': 'property-description-input' },
                }}
              />
            )}
          />
          <CheckboxGroup
            formField={{
              values: applicabilities,
              type: EFormFieldType.CheckboxGroup,
              field: 'applicability',
              rules: { required: { value: true, message: 'applicabilityErrorMessage' } },
            }}
            label={t('labels.applicability')}
            checkFieldDisabled={(_unused1: string, _unused2: string, code: string) =>
              validateApplicability(getValues(), manipulationType, code)
            }
            control={control}
            gridClass="checkbox-grid-3col"
          />

          <fieldset className="inputFieldset w-100 mt-1em">
            <legend className="inputLegend" id="type">
              {t('labels.type')} <span className="asterisk">*</span>
              <span className="property-type-restrict-message">
                {manipulationType === EActionType.Create ? t('labels.restrictUpdate') : ''}
              </span>
            </legend>
            <Controller
              name="type"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <RadioGroup {...field}>
                  <Grid container>
                    {types?.map((value: IEnumOption) => {
                      return (
                        <Grid key={value.code} className="pt-0" size={4}>
                          <FormControlLabel
                            value={value.code}
                            key={value.code}
                            control={<Radio disabled={manipulationType === EActionType.Edit} />}
                            label={value.name}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </RadioGroup>
              )}
            />
          </fieldset>
          {selectedType && renderDefaults(getValues(), types, manipulationType, t, control)}
          {selectedType === EPropertyType.Enum &&
            renderEnumFields(
              getValues(),
              enumOptions,
              enumValues,
              manipulationType,
              setEnumOptions,
              t,
              control
            )}
          {manipulationType === EActionType.Edit && !oldFormData.isActive && (
            <Controller
              name={'isActive'}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox {...field} onChange={(e) => field.onChange(e.target.checked)} />
                  }
                  label={t('labels.isActive')}
                />
              )}
            />
          )}
          <Box className="formActionButtons">
            <Button
              type="submit"
              data-testid="role-button-submit"
              variant="contained"
              size="medium"
              className="text-capitalize"
              disabled={!isValid || !isDirty}
            >
              {manipulationType === EActionType.Create ? t('button.create') : t('button.save')}
            </Button>
            <Button
              size="medium"
              data-testid="role-button-cancel"
              className="text-capitalize"
              onClick={() => {
                reset();
                resetAllErrorInfo();
                setDisplayAlertMessage(false);
              }}
            >
              {t('button.cancel')}
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
};
